<template>
  <v-dialog v-model="isVisible" max-width="460" data-cy="push-confirm-dialog">
    <v-card>
      <v-card-title class="text-h6 font-weight-bold">Submit Lien to QuickBooks</v-card-title>
      <v-card-text>
        <p class="text-body-2 text-medium-emphasis mb-4">
          This will create a trust check in QuickBooks for:
        </p>

        <!-- Payee summary card -->
        <div class="bg-grey-lighten-4 border rounded-lg px-4 py-3 mb-4">
          <div class="text-caption text-medium-emphasis">Payee</div>
          <div class="text-subtitle-1 font-weight-bold text-grey-darken-4">
            {{ lien?.vendorDisplayName }}
          </div>
          <div class="text-caption text-disabled">{{ lien?.lienTypeDisplayName }}</div>
          <div class="d-flex justify-space-between align-center mt-3">
            <span class="text-body-2 text-medium-emphasis">Amount</span>
            <span class="text-h6 font-weight-bold text-grey-darken-4 tabular-nums">
              {{ formatCurrency(lien?.currentAmount ?? 0) }}
            </span>
          </div>
        </div>

        <p class="text-caption text-disabled mb-0" style="line-height: 1.6">
          The lien status will update to <strong>Submitted</strong> and a QuickBooks check reference will be
          recorded. This action can be undone while the check has not been reconciled.
        </p>
      </v-card-text>
      <v-card-actions class="pa-6">
        <LlSecondaryButton data-cy="push-confirm-cancel" @click="cancel">Cancel</LlSecondaryButton>
        <v-spacer />
        <LlSecondaryButton color="primary" data-cy="push-confirm-ok" @click="confirm">
          {{ isRetry ? 'Retry Submit' : 'Submit to QuickBooks' }}
        </LlSecondaryButton>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { LienData } from '@/models/lienTypes';
import { formatCurrency } from '@/services/utils';
import { pushBadgeState } from '@/utils/lienHelpers';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';

const isVisible = ref(false);
const lien = ref<LienData | null>(null);
let resolvePromise: ((value: boolean) => void) | null = null;

const isRetry = computed(() => !!lien.value && pushBadgeState(lien.value) === 'failed');

/** Open the confirmation for a lien; resolves true when confirmed. */
function open(target: LienData): Promise<boolean> {
  lien.value = target;
  isVisible.value = true;
  return new Promise((resolve) => {
    resolvePromise = resolve;
  });
}

function confirm(): void {
  isVisible.value = false;
  resolvePromise?.(true);
  resolvePromise = null;
}

function cancel(): void {
  isVisible.value = false;
  resolvePromise?.(false);
  resolvePromise = null;
}

defineExpose({ open });
</script>
